import { MutableRefObject, ReactNode, useCallback, useEffect, useState } from 'react'
import { showToast, Toast } from 'common/ToastMessage'
import { ReactComponent as MergeIcon } from 'assets/icons/mergeBranch16.svg'
import Foco from 'react-foco'
import { EditOnLayerButton } from 'components/EditOnLayerButton/EditOnLayerButton'
import { Portal } from 'components/Portal/Portal'
import { Layer } from 'types'
import { useRootStore } from 'stores/RootStore'

interface Props {
  actionRef: MutableRefObject<HTMLDivElement | null> | ((node: HTMLDivElement) => void)
}

interface ToastMessageProps extends Props {
  onClickOutside: () => void
}

const showVcmToast = (Message: ReactNode, durationInSeconds: number) =>
  showToast(Message, durationInSeconds, 'vcmToast')

const ToastMessage = ({ onClickOutside, actionRef }: ToastMessageProps) => {
  return (
    <Foco onClickOutside={onClickOutside}>
      <Toast.Root variant="info">
        <Toast.Icon>
          <MergeIcon />
        </Toast.Icon>
        <Toast.Description clamp={false}>
          Version Control Mode - Edits to this doc must be merged in using layers.
        </Toast.Description>
        <Toast.Action ref={actionRef} />
      </Toast.Root>
    </Foco>
  )
}

export const VcmToastMessage = ({ actionRef }: Props) => {
  const [isShown, setIsShown] = useState(true)

  useEffect(() => {
    isShown &&
      showVcmToast(
        <ToastMessage
          actionRef={actionRef}
          onClickOutside={() => {
            setIsShown(false)
            showVcmToast(<ToastMessage actionRef={actionRef} onClickOutside={() => {}} />, 0.1)
          }}
        />,
        0,
      )

    return () => {
      isShown && showVcmToast(<ToastMessage actionRef={actionRef} onClickOutside={() => {}} />, 0.1)
    }
  }, [isShown, actionRef])

  return null
}

interface ContainerProps {
  articleKey: string
  layer: Layer
}

export const VcmToastMessageContainer = ({ articleKey, layer }: ContainerProps) => {
  const rootStore = useRootStore()
  const isVersionControlled = rootStore.docStore?.isVersionControlled || false
  const [showVcmToast, setShowVcmToast] = useState(
    layer?.is_main && isVersionControlled && layer?.permissions?.can_edit,
  )
  const [actionRef, setActionRef] = useState<HTMLDivElement | null>(null)
  const setActionRefCallback = useCallback((node: HTMLDivElement) => setActionRef(node), [])

  useEffect(() => {
    if (!layer) return

    setShowVcmToast(layer.is_main && isVersionControlled && layer.permissions.can_edit)
  }, [layer, isVersionControlled])

  return (
    <>
      {showVcmToast && <VcmToastMessage actionRef={setActionRefCallback} />}
      {actionRef && (
        <Portal container={actionRef}>
          <EditOnLayerButton articleKey={articleKey} variant="ghostWhite" size="md" />
        </Portal>
      )}
    </>
  )
}
